import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { catchError, firstValueFrom, of } from 'rxjs';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppSettings } from './app.settings';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptorConfiguration,
	MsalRedirectComponent,
	MsalService,
} from '@azure/msal-angular';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
	BrowserCacheLocation,
	IPublicClientApplication,
	InteractionType,
	LogLevel,
	PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SharedModule } from './shared/shared.module';
import { LoadingService } from './core/services/loading.service';
import { AppInterceptor } from './core/interceptors/app-interceptor';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { CustomOverlayContainer } from './shared/utils/custom-overlay-container';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { menuScrollStrategy } from './shared/utils/scroll-strategy';
import { CoreModule } from './core/core.module';
import {PosTokenInterceptor} from './core/interceptors/pos-integrity-interceptors';
import {TranslateModule} from '@ngx-translate/core';

import config from './app-config.json';
import { LoadingInterceptor } from './core/interceptors/loading-interceptor';
import { MsgBannerComponent } from './shared/components/msg-banner/msg-banner.component';

import {initializeFaro} from '@grafana/faro-web-sdk';

export const faro = initializeFaro({
  url: environment.alloyUrl,
  app: {
    name: 'myteam-ui',
    version: '1.0.0',
    environment: 'prod'
  }
});

export function loggerCallback(logLevel: LogLevel, message: string) {
	console.debug('client logging' + message);
}

export function loadGoogleMaps(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApi}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });
}

const isIE =
	globalThis.navigator.userAgent.includes('MSIE ') ||
	globalThis.navigator.userAgent.includes('Trident/');

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.clientId,
			authority: config.auth.authority,
			redirectUri: globalThis.location.origin + environment.baseHref,
			postLogoutRedirectUri: globalThis.location.origin + environment.baseHref,
			navigateToLoginRequestUrl: true,
		},
		system: {
			tokenRenewalOffsetSeconds: 300,
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Info,
				piiLoggingEnabled: true,
			},
		},
		cache: {
			/* localStorage on every browser, Safari included. sessionStorage dies with the tab
			   and is not shared across tabs, which forced a fresh login every time Safari users
			   reopened the app. ITP does not clear storage during redirect flows — it applies a
			   7-day inactivity cap that would hit sessionStorage just the same. Temporary
			   artifacts (state, nonce, PKCE verifier) always go to session/memory storage
			   regardless of this setting, so the redirect flow is unaffected. */
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: isIE, // set to true for IE 11
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();
	protectedResourceMap.set(environment.apiResourceUri, [environment.apiScopeUrl]);
	protectedResourceMap.set(config.resources.graphApi.resourceUri, [
		config.resources.graphApi.resourceScope,
	]);
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/users', [
    'user.read'
  ])
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read', 'user.ReadBasic.all'
  ]);

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap,
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: ['user.read'],
		},
	};
}

/**
 * MSAL requires initialize() to resolve before any other MSAL API is called
 * (handleRedirectPromise, acquireTokenSilent, loginRedirect, ...). Running it as an app
 * initializer is what guarantees that ordering for MsalRedirectComponent, the router's
 * initial navigation and the HTTP interceptor alike — doing it inside a component's
 * ngOnInit races all three.
 *
 * Initialization failures are swallowed on purpose: AD users authenticate through a
 * cookie and must still be able to boot the app when MSAL cannot initialize.
 */
export function initializeMsal(): Promise<void> {
	const msalService = inject(MsalService);

	return firstValueFrom(
		msalService.initialize().pipe(
			catchError((error: unknown) => {
				console.error('MSAL initialization failed', error);
				return of(void 0);
			})
		)
	);
}

@NgModule({
	declarations: [
		AppComponent
	],
	bootstrap: [
		AppComponent,
		MsalRedirectComponent
	],
	imports: [
		BrowserModule,
		AppRoutingModule,
		AppRoutingModule,
		NgxSpinnerModule,
		SharedModule,
		CoreModule,
		MsgBannerComponent,
		BrowserAnimationsModule,
		TranslateModule.forRoot()
	],
	providers: [
		[LoadingService],
		provideAppInitializer(initializeMsal),
		provideAppInitializer(loadGoogleMaps),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AppInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: PosTokenInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoadingInterceptor, multi: true
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory,
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory,
		},
		{ provide: OverlayContainer, useClass: CustomOverlayContainer },
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { appearance: 'outline', subscriptSizing: 'dynamic' }
		},
		{
			provide: MAT_MENU_SCROLL_STRATEGY,
			useFactory: menuScrollStrategy,
			deps: [Overlay],
		},
		MsalService,
		MsalGuard,
		AppSettings,
		MsalBroadcastService,
		provideHttpClient(withInterceptorsFromDi()),
	]
})
export class AppModule {}
